import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { BreakpointObserver } from '@angular/cdk/layout';
import { RouterModule } from '@angular/router';
import { Router } from '@angular/router';
import { catchError, finalize, forkJoin, of } from 'rxjs';

import jsPDF from 'jspdf';
import { AppStateService } from '../../app-state.service';
import { FormConfigService } from '../forms-config.service';
import { GoogleMapsService } from '../../google-maps.service';
import { ClinicInformation } from '../clinic.model';
import { PatientProfile } from '../patient.model';
import { PaymentService } from '../payment-center/payment.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ClinicInfoComponent } from '../clinic-info/clinic-info.component';

@Component({
  selector: 'app-patient-summary',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatIconModule,
    RouterModule,
    ClinicInfoComponent
  ],
  templateUrl: './patient-summary.component.html',
  styleUrls: ['./patient-summary.component.scss']
})
export class PatientSummaryComponent implements OnInit {
  paymentsEnabled: boolean = false;
  loading = true;
  
  // Services
  private snackBar = inject(MatSnackBar);
  private formConfigService = inject(FormConfigService);
  private appStateService = inject(AppStateService);
  private googleMapsService = inject(GoogleMapsService);
  private breakpointObserver = inject(BreakpointObserver);
  private router = inject(Router);
  private paymentService = inject(PaymentService);
  
  // Patient and clinic data
  patientId: string;
  clientId: string;
  patient: PatientProfile;
  clinicInfo: ClinicInformation;
  
  // Account data
  accountAgingBalance: any = {};
  balanceItems: any[] = [];
  upcomingAppointments: any[] = [];
  
  // Form controls
  searchControl = new FormControl('');

  constructor() {
    // Get data from app state
    this.clientId = this.appStateService.getState().clientId || '';
    this.patientId = this.appStateService.getState().patientId || '';
    this.patient = this.appStateService.getState().patient || {} as PatientProfile;
    this.clinicInfo = this.appStateService.getState().clinicInfo || {} as ClinicInformation;
  }

  ngOnInit(): void {
    // Load all required data
    this.loadDashboardData();
  }

  /**
   * Load all dashboard data in parallel
   */
  loadDashboardData(): void {
    this.loading = true;
    
    // Create an array of observable requests
    const requests = [
      this.paymentService.fetchAccountAgingBalances(this.clientId, this.patientId).pipe(
        catchError(error => {
          this.showNotification('Unable to load account balance information');
          return of(null);
        })
      ),
      this.paymentService.fetchPaymentsEnabledStatus(this.appStateService.getState().tenantId || '').pipe(
        catchError(error => {
          return of(false);
        })
      )
      // Add more API calls here as needed (appointments, etc.)
    ];
    
    // Execute all requests in parallel
    forkJoin(requests).pipe(
      finalize(() => {
        this.loading = false;
      })
    ).subscribe({
      next: ([balanceData, paymentsEnabled]) => {
        // Process balance data
        if (balanceData) {
          this.accountAgingBalance = this.formatAccountBalanceData(balanceData);
        }
        
        // Set payments enabled status
        this.paymentsEnabled = paymentsEnabled || false;
        this.appStateService.setPaymentsEnabled(this.paymentsEnabled);
      },
      error: (error) => {
        this.showNotification('Something went wrong. Please try again later.');
      }
    });
  }

  /**
   * Handle payment form submission via Angular Router navigation
   */
  submitPaymentForm(): void {
    this.router.navigate(['/dashboard', 'payments']);
  }

  /**
   * Format account balance data
   */
  formatAccountBalanceData(data: any): any {
    return {
      Bal_0_30: data.Bal_0_30 || 0.00,
      Bal_31_60: data.Bal_31_60 || 0.00,
      Bal_61_90: data.Bal_61_90 || 0.00,
      BalOver90: data.BalOver90 || 0.00,
      Total: data.Total || 0.00,
      InsEst: data.InsEst || 0.00,
      EstBal: data.EstBal || 0.00,
      PatEstBal: data.PatEstBal || 0.00,
      Unearned: data.Unearned || 0.00
    };
  }

  /**
   * Open Google Maps with directions to clinic
   */
  navigateToClinic(): void {
    if (!this.clinicInfo.address) {
      this.showNotification('Clinic address is not available');
      return;
    }
    
    const address = this.clinicInfo.address;
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`, '_blank');
  }

  /**
   * Call the clinic phone number
   */
  callClinic(): void {
    if (!this.clinicInfo.phoneNumber) {
      this.showNotification('Clinic phone number is not available');
      return;
    }
    window.location.href = 'tel:' + this.clinicInfo.phoneNumber;
  }

  /**
   * Email the clinic
   */
  emailClinic(): void {
    if (!this.clinicInfo.email) {
      this.showNotification('Clinic email is not available');
      return;
    }
    window.location.href = 'mailto:' + this.clinicInfo.email;
  }

  /**
   * Generate PDF of account summary
   */
  generatePdf(): void {
    this.showNotification('Preparing your summary for download...');

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);
    let yPosition = margin;

    // Helper function to check if we need a new page
    const checkPageBreak = (requiredHeight: number): void => {
      if (yPosition + requiredHeight > pageHeight - margin) {
        pdf.addPage();
        yPosition = margin;
      }
    };

    // Title
    pdf.setFontSize(20);
    pdf.setTextColor(103, 58, 183); // Primary purple color
    pdf.text('Your Account Summary', pageWidth / 2, yPosition + 10, { align: 'center' });
    yPosition += 25;

    // Welcome section
    if (this.patient?.firstName) {
      pdf.setFontSize(16);
      pdf.setTextColor(60, 60, 60);
      pdf.text(`Welcome, ${this.patient.firstName}`, margin, yPosition);
      yPosition += 8;
      pdf.setFontSize(11);
      pdf.setTextColor(120, 120, 120);
      pdf.text("Here's your patient dashboard", margin, yPosition);
      yPosition += 15;
    }

    // Account Summary Section
    checkPageBreak(60);
    pdf.setFontSize(14);
    pdf.setTextColor(103, 58, 183);
    pdf.text('Account Summary', margin, yPosition);
    yPosition += 10;

    // Balance box
    pdf.setFillColor(245, 245, 255);
    pdf.setDrawColor(103, 58, 183);
    pdf.roundedRect(margin, yPosition, contentWidth, 40, 3, 3, 'FD');

    // Current Balance label
    pdf.setFontSize(11);
    pdf.setTextColor(100, 100, 100);
    pdf.text('Your Current Balance', margin + 10, yPosition + 12);

    // Balance amount
    pdf.setFontSize(24);
    pdf.setTextColor(103, 58, 183);
    const balanceAmount = this.accountAgingBalance?.PatEstBal || 0;
    pdf.text(`$${balanceAmount.toFixed(2)}`, margin + 10, yPosition + 28);

    // Balance status message
    if (this.accountAgingBalance?.Total <= 0) {
      pdf.setFontSize(10);
      pdf.setTextColor(76, 175, 80); // Green
      pdf.text('Your account is paid in full. Thank you!', margin + 80, yPosition + 25);
    }

    yPosition += 50;

    // Balance breakdown
    checkPageBreak(50);
    pdf.setFontSize(12);
    pdf.setTextColor(60, 60, 60);
    pdf.text('Balance Breakdown', margin, yPosition);
    yPosition += 8;

    const balanceItems = [
      { label: 'Total Charges', value: this.accountAgingBalance?.Total || 0 },
      { label: 'Insurance Estimate', value: this.accountAgingBalance?.InsEst || 0 },
      { label: 'Patient Responsibility', value: this.accountAgingBalance?.PatEstBal || 0 },
      { label: '0-30 Days', value: this.accountAgingBalance?.Bal_0_30 || 0 },
      { label: '31-60 Days', value: this.accountAgingBalance?.Bal_31_60 || 0 },
      { label: '61-90 Days', value: this.accountAgingBalance?.Bal_61_90 || 0 },
      { label: 'Over 90 Days', value: this.accountAgingBalance?.BalOver90 || 0 }
    ];

    balanceItems.forEach((item) => {
      checkPageBreak(8);
      pdf.setFontSize(10);
      pdf.setTextColor(80, 80, 80);
      pdf.text(item.label, margin + 5, yPosition);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`$${item.value.toFixed(2)}`, margin + 80, yPosition);
      yPosition += 7;
    });

    yPosition += 10;

    // Clinic Information Section
    if (this.clinicInfo) {
      checkPageBreak(60);
      pdf.setFontSize(14);
      pdf.setTextColor(103, 58, 183);
      pdf.text('Your Clinic', margin, yPosition);
      yPosition += 10;

      pdf.setFontSize(11);
      pdf.setTextColor(60, 60, 60);

      if (this.clinicInfo.displayName) {
        pdf.text(this.clinicInfo.displayName, margin + 5, yPosition);
        yPosition += 7;
      }

      if (this.clinicInfo.address) {
        const addressLines = pdf.splitTextToSize(this.clinicInfo.address, contentWidth - 10);
        addressLines.forEach((line: string) => {
          pdf.text(line, margin + 5, yPosition);
          yPosition += 6;
        });
      }

      if (this.clinicInfo.phoneNumber) {
        yPosition += 2;
        pdf.text(`Phone: ${this.clinicInfo.phoneNumber}`, margin + 5, yPosition);
        yPosition += 7;
      }

      if (this.clinicInfo.email) {
        pdf.text(`Email: ${this.clinicInfo.email}`, margin + 5, yPosition);
        yPosition += 7;
      }
    }

    // Footer with generation date
    yPosition = pageHeight - 25;
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 5;

    pdf.setFontSize(9);
    pdf.setTextColor(150, 150, 150);
    const generatedDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    pdf.text(`Generated on ${generatedDate}`, pageWidth / 2, yPosition, { align: 'center' });
    pdf.text('Page 1 of 1', pageWidth / 2, yPosition + 5, { align: 'center' });

    pdf.save('account-summary.pdf');
  }

  /**
   * Show notification message
   */
  showNotification(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
    });
  }
}